<template>
  <div class="app-container">
    <!-- 添加作业标题和详情链接 -->
    <div class="homework-header">
      <div class="homework-title">作业 <span class="homework-count">(共发布{{ homeworkCount }}个)</span></div>
      <router-link to="/score-analysis/homework/list" class="detail-link">详情 <i class="el-icon-arrow-right"></i></router-link>
    </div>

    <!-- 统计卡片区域 -->
    <div class="stats-container">
      <div class="stat-card">
        <div class="stat-title">平均完成率</div>
        <div class="stat-value">{{ completionRate }}%</div>
        <el-progress type="circle" :percentage="completionRate" :width="80"></el-progress>
      </div>

      <div class="stat-card">
        <div class="stat-title">平均分</div>
        <div class="stat-value">{{ averageScore }} <span class="unit">分</span></div>
        <el-progress type="circle" :percentage="scorePercentage" :width="80" :color="scoreColors"></el-progress>
      </div>
    </div>

    <!-- 分数分布区域 -->
    <div class="score-distribution">
      <div class="distribution-title">分数分布</div>
      <div class="distribution-chart">
        <el-row :gutter="20">
          <el-col :span="16">
            <div ref="scoreChart" style="width: 100%; height: 300px;"></div>
          </el-col>
          <el-col :span="8">
            <div class="score-legend">
              <div class="legend-item">
                <div class="color-block" style="background-color: #409EFF;"></div>
                <div class="legend-text">60分以下 {{ lowScoreCount }} 人</div>
              </div>
              <div class="legend-item">
                <div class="color-block" style="background-color: #67C23A;"></div>
                <div class="legend-text">60~79分 {{ midScoreCount }} 人</div>
              </div>
              <div class="legend-item">
                <div class="color-block" style="background-color: #E6A23C;"></div>
                <div class="legend-text">80~100分 {{ highScoreCount }} 人</div>
              </div>
              <div class="legend-item">
                <div class="color-block" style="background-color: #F56C6C;"></div>
                <div class="legend-text">未获得分数 {{ noScoreCount }} 人</div>
              </div>
            </div>
          </el-col>
        </el-row>
      </div>
    </div>

    <div class="no-score-note">未获得分数：学生未提交作业，或作业未被批阅</div>

    <router-view></router-view>
  </div>
</template>

<script>
import { mapGetters } from 'vuex'
import axios from 'axios'
import * as echarts from 'echarts'

export default {
  name: 'homework-analysis',
  data() {
    return {
      homeworkCount: 19, // 添加作业数量
      completionRate: 45, // 平均完成率
      averageScore: 82, // 平均分
      lowScoreCount: 3, // 60分以下人数
      midScoreCount: 40, // 60-79分人数
      highScoreCount: 74, // 80-100分人数
      noScoreCount: 2, // 未获得分数人数
      homeworkList: [], // 作业列表
      submissions: [], // 提交记录
      scoreChart: null, // 图表实例
      loading: false,
      roles: window.localStorage.getItem('roles') // 从localStorage获取roles
    }
  },
  computed: {
    ...mapGetters([
      // 'roles' 已从这里移除
    ]),
    // 计算平均分对应的百分比（用于进度条）
    scorePercentage() {
      return Math.min(100, Math.round(this.averageScore))
    },
    // 分数进度条颜色
    scoreColors() {
      return [
        { color: '#F56C6C', percentage: 60 },
        { color: '#E6A23C', percentage: 75 },
        { color: '#67C23A', percentage: 90 },
        { color: '#409EFF', percentage: 100 }
      ]
    }
  },
  created() {
    // 检查用户角色
    if (!this.roles || !this.roles.includes('teacher')) {
      this.$message.error('您没有权限访问此页面')
      this.$router.push('/404')
    }
  },
  mounted() {
    this.fetchHomeworkData()
    this.initScoreChart()
  },
  methods: {
    // 获取作业数据
    async fetchHomeworkData() {
      this.loading = true
      try {
        // 获取作业列表
        const homeworkRes = await axios.get('http://localhost:8080/api/homework/list')
        if (homeworkRes.data && homeworkRes.data.code === 1) {
          this.homeworkList = homeworkRes.data.data
          // 更新作业数量
          this.homeworkCount = this.homeworkList.length

          // 获取提交记录
          const submissionsRes = await axios.get('http://localhost:8080/api/student-submissions')
          if (submissionsRes.data) {
            this.submissions = submissionsRes.data
            this.calculateStatistics()
          }
        }
      } catch (error) {
        console.error('获取作业数据失败:', error)
      } finally {
        this.loading = false
      }
    },

    // 计算统计数据
    calculateStatistics() {
      if (!this.submissions || this.submissions.length === 0) {
        return
      }

      // 计算完成率
      const totalSubmissions = this.submissions.length
      const completedSubmissions = this.submissions.filter(s => s.status === '已批改').length
      this.completionRate = Math.round((completedSubmissions / totalSubmissions) * 100)

      // 计算平均分
      const scoredSubmissions = this.submissions.filter(s => s.score !== null && s.score !== undefined)
      if (scoredSubmissions.length > 0) {
        const totalScore = scoredSubmissions.reduce((sum, s) => sum + s.score, 0)
        this.averageScore = Math.round(totalScore / scoredSubmissions.length)
      }

      // 计算分数分布
      this.lowScoreCount = this.submissions.filter(s => s.score !== null && s.score < 60).length
      this.midScoreCount = this.submissions.filter(s => s.score >= 60 && s.score < 80).length
      this.highScoreCount = this.submissions.filter(s => s.score >= 80 && s.score <= 100).length
      this.noScoreCount = this.submissions.filter(s => s.score === null || s.score === undefined).length

      // 更新图表
      this.updateScoreChart()
    },

    // 初始化分数分布图表
    initScoreChart() {
      this.$nextTick(() => {
        if (this.$refs.scoreChart) {
          this.scoreChart = echarts.init(this.$refs.scoreChart)
          this.updateScoreChart()
        }
      })
    },

    // 更新分数分布图表
    updateScoreChart() {
      if (!this.scoreChart) return

      const option = {
        tooltip: {
          trigger: 'item',
          formatter: '{b}: {c} ({d}%)'
        },
        series: [
          {
            name: '分数分布',
            type: 'pie',
            radius: ['50%', '70%'],
            avoidLabelOverlap: false,
            label: {
              show: false,
              position: 'center'
            },
            emphasis: {
              label: {
                show: true,
                fontSize: '18',
                fontWeight: 'bold'
              }
            },
            labelLine: {
              show: false
            },
            data: [
              { value: this.lowScoreCount, name: '60分以下', itemStyle: { color: '#409EFF' } },
              { value: this.midScoreCount, name: '60~79分', itemStyle: { color: '#67C23A' } },
              { value: this.highScoreCount, name: '80~100分', itemStyle: { color: '#E6A23C' } },
              { value: this.noScoreCount, name: '未获得分数', itemStyle: { color: '#F56C6C' } }
            ]
          }
        ]
      }

      this.scoreChart.setOption(option)
    }
  },
  beforeDestroy() {
    // 销毁图表实例
    if (this.scoreChart) {
      this.scoreChart.dispose()
      this.scoreChart = null
    }
  }
}
</script>

<style scoped>
/* 添加作业标题和详情链接样式 */
.homework-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
}

.homework-title {
  font-size: 20px;
  font-weight: bold;
  color: #303133;
}

.homework-count {
  font-size: 16px;
  font-weight: normal;
  color: #909399;
}

.detail-link {
  font-size: 14px;
  color: #909399;
  text-decoration: none;
  display: flex;
  align-items: center;
}

.detail-link:hover {
  color: #409EFF;
}

.detail-link i {
  margin-left: 5px;
}

/* 现有样式 */
.stats-container {
  display: flex;
  margin-bottom: 20px;
}

.stat-card {
  flex: 1;
  background-color: #fff;
  border-radius: 8px;
  padding: 20px;
  margin-right: 20px;
  box-shadow: 0 2px 12px 0 rgba(0, 0, 0, 0.1);
  display: flex;
  flex-direction: column;
  align-items: center;
}

.stat-title {
  font-size: 16px;
  color: #606266;
  margin-bottom: 10px;
}

.stat-value {
  font-size: 36px;
  font-weight: bold;
  color: #303133;
  margin-bottom: 15px;
}

.unit {
  font-size: 16px;
  font-weight: normal;
}

.score-distribution {
  background-color: #fff;
  border-radius: 8px;
  padding: 20px;
  margin-bottom: 20px;
  box-shadow: 0 2px 12px 0 rgba(0, 0, 0, 0.1);
}

.distribution-title {
  font-size: 18px;
  font-weight: bold;
  margin-bottom: 20px;
}

.score-legend {
  height: 100%;
  display: flex;
  flex-direction: column;
  justify-content: center;
}

.legend-item {
  display: flex;
  align-items: center;
  margin-bottom: 15px;
}

.color-block {
  width: 20px;
  height: 20px;
  border-radius: 4px;
  margin-right: 10px;
}

.legend-text {
  font-size: 14px;
  color: #606266;
}

.no-score-note {
  font-size: 14px;
  color: #909399;
  margin-top: 10px;
}
</style>
